import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { Briefcase, TrendingUp, Building2, LogOut, ArrowLeft, ArrowRight, Layers, Wallet, User, Coins, Gavel, Clock, CheckCircle2, XCircle, Bell, CalendarClock, Vote, ShieldCheck, Network } from "lucide-react";
import { api } from "@/lib/api";
import { nok } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { OwnershipTree } from "@/components/OwnershipTree";
import { PortfolioHistoryChart } from "@/components/PortfolioHistoryChart";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-4))", "hsl(var(--chart-2))", "hsl(var(--chart-5))", "hsl(var(--chart-3))", "#8b5cf6", "#0ea5e9"];

const DIV_STATUS = {
  foreslått: { label: "Foreslått", cls: "border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400", Icon: Clock },
  vedtatt: { label: "Vedtatt", cls: "border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400", Icon: Gavel },
  utbetalt: { label: "Utbetalt", cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400", Icon: CheckCircle2 },
  forkastet: { label: "Forkastet", cls: "border-rose-500/30 bg-rose-500/10 text-rose-600 dark:text-rose-400", Icon: XCircle },
};

function initials(n = "") {
  return n.split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
}

function M(n) {
  return `${((n || 0) / 1e6).toLocaleString("nb-NO", { maximumFractionDigits: 2 })} M kr`;
}

export default function Portfolio() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [perspective, setPerspective] = useState("person");
  const [dividends, setDividends] = useState(null);
  const [notifs, setNotifs] = useState(null);
  const [tree, setTree] = useState(null);

  useEffect(() => {
    setData(null);
    setTree(null);
    const params = perspective === "person" ? {} : { perspective };
    api.get(`/portfolio`, { params })
      .then((r) => setData(r.data))
      .catch(() => setData({ holdings: [], perspectives: [] }));
    api.get(`/portfolio/tree`, { params })
      .then((r) => setTree(r.data))
      .catch(() => setTree(null));
  }, [perspective]);

  useEffect(() => {
    api.get(`/portfolio/dividends`).then((r) => setDividends(r.data.dividends || [])).catch(() => setDividends([]));
  }, []);

  useEffect(() => {
    api.get(`/portfolio/notifications`).then((r) => setNotifs(r.data.notifications || [])).catch(() => setNotifs([]));
  }, []);

  const dividendPaid = (dividends || []).filter((d) => d.status === "utbetalt").reduce((s, d) => s + (d.my_amount || 0), 0);
  const paidByYear = {};
  (dividends || []).forEach((d) => { if (d.status === "utbetalt") paidByYear[d.year] = (paidByYear[d.year] || 0) + (d.my_amount || 0); });
  const dividendChart = Object.entries(paidByYear).map(([year, value]) => ({ year, value })).sort((a, b) => Number(a.year) - Number(b.year));
  const kompakt = (v) => ((v || 0) >= 1e6 ? `${(v / 1e6).toFixed(1)} M` : `${Math.round((v || 0) / 1000)} k`);

  const totalValue = data?.total_value || 0;
  const pieData = (data?.holdings || []).filter((h) => h.value > 0).map((h) => ({ name: h.name, value: h.value }));

  return (
    <div className="min-h-screen bg-secondary/30">
      <header className="sticky top-0 z-40 flex h-16 items-center border-b border-border/60 bg-background/80 px-5 backdrop-blur-xl sm:px-8">
        <Logo subtitle="Portefølje" />
        <div className="ml-auto flex items-center gap-1.5">
          <ThemeToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu" className="flex items-center gap-2 rounded-full p-1 transition-colors hover:bg-muted">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.avatar || undefined} />
                  <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">{initials(user?.name)}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs font-normal text-muted-foreground">{user?.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="menu-profile-link" onClick={() => navigate("/profil")}>
                <User className="mr-2 h-4 w-4" /> Min profil
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }}>
                <LogOut className="mr-2 h-4 w-4" /> Logg ut
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-8 sm:px-8 lg:py-12">
        <button onClick={() => navigate("/hjem")} className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground" data-testid="back-dashboard">
          <ArrowLeft className="h-4 w-4" /> Til selskaper
        </button>

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Min portefølje</p>
            <h1 className="mt-2 font-headings text-3xl font-extrabold tracking-tight sm:text-4xl">
              Oversikt over eierskapet ditt
            </h1>
            <p className="mt-1.5 max-w-xl text-sm text-muted-foreground">
              Direkte og indirekte eierskap på tvers av selskaper – beregnet oppover gjennom holdingstrukturen.
            </p>
          </div>
          {data?.perspectives?.length ? (
            <Select value={perspective} onValueChange={setPerspective}>
              <SelectTrigger className="w-64" data-testid="perspective-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {data.perspectives.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : null}
        </div>

        {/* KPI cards */}
        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <div className="rounded-xl border border-border/60 bg-card p-5 shadow-sm">
            <Wallet className="h-5 w-5 text-primary" strokeWidth={1.7} />
            <div className="mt-3 font-headings text-2xl font-extrabold">{data ? M(totalValue) : <Skeleton className="h-7 w-24" />}</div>
            <p className="text-xs text-muted-foreground">Total porteføljeverdi</p>
            {data ? (
              <div className="mt-3 flex flex-wrap gap-1.5">
                <span className="rounded-full bg-blue-500/10 px-2 py-0.5 text-[10px] font-medium text-blue-700 dark:text-blue-300">Direkte {M(data.direct_value)}</span>
                <span className="rounded-full bg-violet-500/10 px-2 py-0.5 text-[10px] font-medium text-violet-700 dark:text-violet-300">Indirekte {M(data.indirect_value)}</span>
              </div>
            ) : null}
          </div>
          <div className="rounded-xl border border-border/60 bg-card p-5 shadow-sm">
            <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" strokeWidth={1.7} />
            <div className="mt-3 font-headings text-2xl font-extrabold">{data ? M(data.direct_value) : <Skeleton className="h-7 w-20" />}</div>
            <p className="text-xs text-muted-foreground">Direkte eierskap</p>
          </div>
          <div className="rounded-xl border border-border/60 bg-card p-5 shadow-sm">
            <Layers className="h-5 w-5 text-violet-600 dark:text-violet-400" strokeWidth={1.7} />
            <div className="mt-3 font-headings text-2xl font-extrabold">{data ? M(data.indirect_value) : <Skeleton className="h-7 w-20" />}</div>
            <p className="text-xs text-muted-foreground">Indirekte eierskap</p>
          </div>
          <div className="rounded-xl border border-border/60 bg-card p-5 shadow-sm">
            <Briefcase className="h-5 w-5 text-primary" strokeWidth={1.7} />
            <div className="mt-3 font-headings text-2xl font-extrabold">{data ? data.companies_count : <Skeleton className="h-7 w-10" />}</div>
            <p className="text-xs text-muted-foreground">Selskaper</p>
            {data && data.controlled_count > 0 ? (
              <div className="mt-3">
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300" data-testid="portfolio-controlled-count">
                  <ShieldCheck className="h-3 w-3" /> Kontroll i {data.controlled_count}
                </span>
              </div>
            ) : null}
          </div>
        </div>

        {/* Eierkart / holdingstruktur */}
        <div className="mt-6 rounded-xl border border-border/60 bg-card shadow-sm" data-testid="ownership-tree-card">
          <div className="flex items-center gap-2 border-b border-border/60 px-6 py-4">
            <Network className="h-4 w-4 text-primary" />
            <h3 className="font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">Eierkart</h3>
            <span className="ml-auto text-xs text-muted-foreground">Blå = direkte · lilla = eid via et selskap</span>
          </div>
          <div className="p-6">
            {tree === null ? (
              <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>
            ) : (
              <OwnershipTree tree={tree} onSelect={(id) => navigate(`/selskap/${id}`)} />
            )}
          </div>
        </div>

        {notifs && notifs.length > 0 ? (
          <div className="mt-8 rounded-xl border border-border/60 bg-card shadow-sm" data-testid="portfolio-notifications">
            <div className="flex items-center gap-2 border-b border-border/60 px-6 py-4">
              <Bell className="h-4 w-4 text-primary" />
              <h3 className="font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">Varsler</h3>
              <span className="ml-auto rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">{notifs.length}</span>
            </div>
            <div className="max-h-80 divide-y divide-border/60 overflow-auto">
              {notifs.map((n) => {
                const isDiv = n.kind === "dividend";
                const Icon = isDiv ? Coins : CalendarClock;
                const goMain = () => navigate(n.kind === "meeting" && n.meeting_id ? `/selskap/${n.company_id}/moter/${n.meeting_id}` : `/selskap/${n.company_id}`);
                return (
                  <div
                    key={n.id}
                    data-testid={`notif-${n.id}`}
                    className="group flex w-full items-start gap-3 px-6 py-3.5 transition-colors hover:bg-muted/50"
                  >
                    <div className={`mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg ${isDiv ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-blue-500/10 text-blue-600 dark:text-blue-400"}`}>
                      <Icon className="h-4 w-4" strokeWidth={1.8} />
                    </div>
                    <button onClick={goMain} data-testid={`notif-open-${n.id}`} className="min-w-0 flex-1 text-left">
                      <p className="truncate text-sm font-medium group-hover:text-primary">{n.title}</p>
                      <p className="truncate text-xs text-muted-foreground">{n.company_name} · {n.subtitle}</p>
                    </button>
                    {n.prevote?.status === "todo" ? (
                      <Button size="sm" variant="outline" className="h-8 shrink-0 gap-1.5" data-testid={`notif-prevote-${n.id}`} onClick={() => navigate(n.prevote.path)}>
                        <Vote className="h-3.5 w-3.5" /> Forhåndsstem
                      </Button>
                    ) : n.prevote?.status === "done" ? (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400" data-testid={`notif-prevoted-${n.id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Forhåndsstemt
                      </span>
                    ) : (
                      <ArrowRight className="mt-1 h-4 w-4 shrink-0 -translate-x-1 text-muted-foreground opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="mt-6">
          <PortfolioHistoryChart perspective={perspective} />
        </div>

        {dividends && dividends.length > 0 ? (
          <div className="mt-6 rounded-xl border border-border/60 bg-card shadow-sm" data-testid="portfolio-dividends">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 px-6 py-4">
              <h3 className="flex items-center gap-1.5 font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">
                <Coins className="h-4 w-4 text-primary" /> Mine utbytter
              </h3>
              {dividendPaid > 0 ? (
                <span className="text-xs text-muted-foreground">Sum utbetalt: <span className="font-semibold text-emerald-600 dark:text-emerald-400">{nok(dividendPaid)}</span></span>
              ) : null}
            </div>
            {dividendChart.length > 0 ? (
              <div className="border-b border-border/60 px-6 py-5" data-testid="portfolio-dividend-chart">
                <p className="mb-2 text-xs font-medium text-muted-foreground">Utbetalt utbytte per år</p>
                <div className="h-44">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dividendChart} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis dataKey="year" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={kompakt} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={44} />
                      <Tooltip
                        cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }}
                        formatter={(v) => [nok(v), "Utbetalt"]}
                        labelFormatter={(l) => `Regnskapsår ${l}`}
                        contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--popover))", color: "hsl(var(--popover-foreground))", fontSize: 12 }}
                      />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]} fill="hsl(var(--chart-1))" maxBarSize={52} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : null}
            <div className="divide-y divide-border/60">
              {dividends.map((d) => {
                const st = DIV_STATUS[d.status] || DIV_STATUS.foreslått;
                return (
                  <button
                    key={d.dividend_id}
                    data-testid={`portfolio-dividend-${d.dividend_id}`}
                    onClick={() => navigate(`/selskap/${d.company_id}`)}
                    className="group flex w-full flex-wrap items-center justify-between gap-3 px-6 py-4 text-left transition-colors hover:bg-muted/50"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium group-hover:text-primary">{d.company_name}</p>
                      <p className="text-xs text-muted-foreground">Regnskapsår {d.year} · eierandel {d.ownership_pct}%</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${st.cls}`}>
                        <st.Icon className="h-3.5 w-3.5" /> {st.label}
                      </span>
                      <p className="font-headings text-base font-bold">{nok(d.my_amount)}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="mt-6 grid gap-6 lg:grid-cols-5">
          {/* Donut */}
          <div className="rounded-xl border border-border/60 bg-card p-6 shadow-sm lg:col-span-2">
            <h3 className="font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">Fordeling per selskap</h3>
            {data ? (
              pieData.length ? (
                <div className="relative mt-2 h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={62} outerRadius={90} paddingAngle={2} stroke="none">
                        {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip
                        formatter={(v, n) => [M(v), n]}
                        contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--popover))", color: "hsl(var(--popover-foreground))", fontSize: 12 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                    <p className="font-headings text-xl font-extrabold">{M(totalValue)}</p>
                    <p className="text-xs text-muted-foreground">samlet verdi</p>
                  </div>
                </div>
              ) : (
                <p className="mt-6 text-sm text-muted-foreground">Ingen verdsatte poster i denne visningen.</p>
              )
            ) : (
              <Skeleton className="mt-4 h-56 rounded-lg" />
            )}
            <div className="mt-4 space-y-2">
              {(data?.holdings || []).map((h, i) => (
                <div key={h.company_id} className="flex items-center gap-2 text-sm">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                  <span className="min-w-0 flex-1 truncate">{h.name}</span>
                  <span className="font-medium">{M(h.value)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Holdings list */}
          <div className="lg:col-span-3">
            <div className="rounded-xl border border-border/60 bg-card shadow-sm">
              <div className="border-b border-border/60 px-6 py-4">
                <h3 className="font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">Eierandeler</h3>
              </div>
              {data === null ? (
                <div className="space-y-2 p-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}</div>
              ) : (data.holdings || []).length === 0 ? (
                <div className="p-10 text-center text-sm text-muted-foreground">Ingen eierandeler i denne visningen.</div>
              ) : (
                <div className="divide-y divide-border/60">
                  {data.holdings.map((h, i) => (
                    <motion.button
                      key={h.company_id}
                      data-testid={`portfolio-holding-${h.company_id}`}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.04 }}
                      onClick={() => navigate(`/selskap/${h.company_id}`)}
                      className="group flex w-full items-center gap-4 px-6 py-4 text-left transition-colors hover:bg-muted/50"
                    >
                      <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                        <Building2 className="h-5 w-5" strokeWidth={1.7} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium group-hover:text-primary">{h.name}</p>
                        <p className="text-xs text-muted-foreground">{h.industry} · {h.city}</p>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {h.direct_pct > 0 ? <span className="rounded-full bg-blue-500/10 px-2 py-0.5 text-[10px] font-medium text-blue-700 dark:text-blue-300">Direkte {h.direct_pct}%</span> : null}
                          {h.indirect_pct > 0 ? <span className="rounded-full bg-violet-500/10 px-2 py-0.5 text-[10px] font-medium text-violet-700 dark:text-violet-300">Indirekte {h.indirect_pct}%</span> : null}
                          {h.controlled ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300" data-testid={`holding-control-${h.company_id}`}><ShieldCheck className="h-3 w-3" /> Kontroll</span> : null}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-headings text-base font-bold">{h.effective_pct}%</p>
                        <p className="text-xs text-muted-foreground">{M(h.value)}</p>
                      </div>
                      <ArrowRight className="h-4 w-4 shrink-0 -translate-x-1 text-muted-foreground opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                    </motion.button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
